import { Modal } from "~/jose_modern/modal";
import { Flash } from "~/jose_modern/flash";
import { fetchScript } from "~/recipal-app/controllers/shared/requests";
import { LOGO_CONSTANTS } from "./constants.js";

// Re-export constants for backwards compatibility
export { LOGO_CONSTANTS };

// Label type configurations for route selection
const LABEL_CONFIGS = {
  recipe: {
    resourceName: "recipe_labels",
    getLabelData: () => window.recipe_label,
  },
  supplement: {
    resourceName: "supplement_labels",
    getLabelData: () => window.supplement_label,
  },
};

// Current config - set during setup()
let currentConfig = LABEL_CONFIGS.recipe;

const getLogosPath = (labelId) => `/${currentConfig.resourceName}/${labelId}/logos`;
const getLogoPath = (labelId) => `/${currentConfig.resourceName}/${labelId}/logo`;
const getToggleDisplayPath = (labelId) => `${getLogoPath(labelId)}/toggle_display`;
const getParamKey = () =>
  currentConfig.resourceName === "supplement_labels" ? "supplement_label" : "recipe_label";

// ==========================================
// Constants
// ==========================================

const LOGO_WRAPPER = "#label-logo-wrapper";

const MODAL = "#logo-modal";
const LOGO_NAME_INPUT = "#logo-name";
const LOGO_FILE_INPUT = "#logo-file";
const LOGO_UPLOAD_SUBMIT_BTN = "#logo-upload-submit";
const LOGO_GRID_ITEM = ".logo-card";
const LOGO_GRID_SELECT_BUTTON = "button.select-logo";
const LOGO_GRID_DELETE_BUTTON = "button.delete-logo";
const DROPZONE_SELECTOR = "#logo-dropzone";

const SIDEBAR_LOGO_TOGGLE = "#hide-logo-toggle";
const SIDEBAR_LOGO_HINT = "#logo-hint";
const SIDEBAR_LOGO_EDIT = "#edit-logo-link";
const SIDEBAR_LOGO_SCALE_SECTION = "#sidebar-logo-scale-section";
const SIDEBAR_LOGO_SCALE_SLIDER = "#sidebar-logo-scale-slider";
const SIDEBAR_LOGO_SCALE_INPUT = "#sidebar-logo-scale";
const SIDEBAR_LOGO_SCALE_VALUE = "#sidebar-logo-scale-value";
const SIDEBAR_LOGO_SCALE_RESET = "#sidebar-logo-size-reset";

const SIDEBAR_LOGO_MARGIN_SLIDER = "#sidebar-logo-margin-slider";
const SIDEBAR_LOGO_MARGIN_INPUT = "#sidebar-logo-margin";
const SIDEBAR_LOGO_MARGIN_VALUE = "#sidebar-logo-margin-value";
const SIDEBAR_LOGO_MARGIN_RESET = "#sidebar-logo-margin-reset";

const LOGO_UPDATE_ERROR = "Error updating logo. Please try again.";

// Centralized error handler that also requests store reload for supplement labels
function handleError(message, error) {
  console.error("[logo.js]", message, error);
  Flash.error(message);

  // DO NOT reload on logo errors - it destroys the modal and breaks event listeners
  // The modal needs to stay open so user can retry
}

// Import constants for local use
const {
  MIN_SCALE,
  MAX_SCALE,
  SCALE_STEP,
  MIN_MARGIN,
  MAX_MARGIN,
  MARGIN_STEP,
  DEFAULT_MARGIN: DEFAULT_MARGIN_PX,
} = LOGO_CONSTANTS;

// Track bound event handlers for cleanup
const boundHandlers = new Map();

// ==========================================
// Helpers
// ==========================================

const getLabelData = () => currentConfig.getLabelData();

const getScale = (optimisticValue) =>
  parseFloat(optimisticValue || getLabelData()?.logo_scale || 1.0);

const getMargin = (optimisticValue) =>
  parseInt(optimisticValue ?? getLabelData()?.logo_margin ?? 8, 10);

// Wraps shared fetchScript with in-flight tracking so the supplement
// editor's autosave can defer until logo writes complete.
async function fetchLogoScript(url, options = {}) {
  const isMutating = options.method && options.method !== "GET";
  if (isMutating) {
    window.__logoWritesInFlight = (window.__logoWritesInFlight || 0) + 1;
  }
  try {
    await fetchScript(url, options);
  } finally {
    if (isMutating) {
      window.__logoWritesInFlight = Math.max(0, (window.__logoWritesInFlight || 1) - 1);
    }
  }
}

// ==========================================
// Logo scale and UI updates
// ==========================================

const updateLogoScaleUI = (scale) => {
  applyScale(scale);
  updateSizeResetButtonVisibility();
};

const updateLogoMarginUI = (margin) => {
  applyMargin(margin);
  updateMarginResetButtonVisibility();
};

function applyScale(scale) {
  const wrapper = document.querySelector(LOGO_WRAPPER);
  if (!wrapper) return;
  wrapper.style.setProperty("--scale", scale.toFixed(2));
}

function applyMargin(margin) {
  const wrapper = document.querySelector(LOGO_WRAPPER);
  if (!wrapper) return;
  wrapper.style.setProperty("--margin", `${margin}px`);
}

function updateSizeResetButtonVisibility() {
  const input = document.querySelector(SIDEBAR_LOGO_SCALE_INPUT);
  const resetBtn = document.querySelector(SIDEBAR_LOGO_SCALE_RESET);
  if (!input || !resetBtn) return;

  const currentScale = parseFloat(input.value);
  resetBtn.classList.toggle("is-hidden", currentScale === 1.0);
}

function updateMarginResetButtonVisibility() {
  const input = document.querySelector(SIDEBAR_LOGO_MARGIN_INPUT);
  const resetBtn = document.querySelector(SIDEBAR_LOGO_MARGIN_RESET);
  if (!input || !resetBtn) return;

  const currentMargin = parseInt(input.value, 10);
  resetBtn.classList.toggle("is-hidden", currentMargin === DEFAULT_MARGIN_PX);
}

function setupSidebarScaleSlider() {
  const slider = document.querySelector(SIDEBAR_LOGO_SCALE_SLIDER);
  const input = document.querySelector(SIDEBAR_LOGO_SCALE_INPUT);
  const valueDisplay = document.querySelector(SIDEBAR_LOGO_SCALE_VALUE);
  const resetBtn = document.querySelector(SIDEBAR_LOGO_SCALE_RESET);

  if (!slider || !input) return;

  // Configure as range input
  slider.type = "range";
  slider.min = MIN_SCALE;
  slider.max = MAX_SCALE;
  slider.step = SCALE_STEP;

  const currentScale = getLabelData()?.logo_scale ?? 1.0;
  slider.value = currentScale;
  input.value = currentScale;

  if (valueDisplay) {
    valueDisplay.textContent = parseFloat(currentScale).toFixed(2) + "x";
  }

  // Remove old handlers
  if (boundHandlers.has("scaleSliderInput")) {
    slider.removeEventListener("input", boundHandlers.get("scaleSliderInput"));
  }
  if (boundHandlers.has("scaleSliderChange")) {
    slider.removeEventListener("change", boundHandlers.get("scaleSliderChange"));
  }

  // Input handler - real-time visual update
  const inputHandler = () => {
    const value = parseFloat(slider.value);
    input.value = value;
    if (valueDisplay) {
      valueDisplay.textContent = value.toFixed(2) + "x";
    }
    updateLogoScaleUI(value);
  };

  // Change handler - persist to server when done sliding
  const changeHandler = () => {
    const value = parseFloat(slider.value);
    persistLogoScale(value).catch(console.error);
  };

  boundHandlers.set("scaleSliderInput", inputHandler);
  boundHandlers.set("scaleSliderChange", changeHandler);

  slider.addEventListener("input", inputHandler);
  slider.addEventListener("change", changeHandler);

  // Reset button
  if (resetBtn) {
    if (boundHandlers.has("scaleResetClick")) {
      resetBtn.removeEventListener("click", boundHandlers.get("scaleResetClick"));
    }

    const resetHandler = (e) => {
      e.preventDefault();
      const defaultScale = 1.0;

      slider.value = defaultScale;
      input.value = defaultScale;
      if (valueDisplay) {
        valueDisplay.textContent = defaultScale.toFixed(2) + "x";
      }
      updateLogoScaleUI(defaultScale);
      persistLogoScale(defaultScale).catch(console.error);
      resetBtn.classList.add("is-hidden");
    };

    boundHandlers.set("scaleResetClick", resetHandler);
    resetBtn.addEventListener("click", resetHandler);
  }
}

function setupSidebarMarginSlider() {
  const slider = document.querySelector(SIDEBAR_LOGO_MARGIN_SLIDER);
  const input = document.querySelector(SIDEBAR_LOGO_MARGIN_INPUT);
  const valueDisplay = document.querySelector(SIDEBAR_LOGO_MARGIN_VALUE);
  const resetBtn = document.querySelector(SIDEBAR_LOGO_MARGIN_RESET);

  if (!slider || !input) return;

  // Configure as range input
  slider.type = "range";
  slider.min = MIN_MARGIN;
  slider.max = MAX_MARGIN;
  slider.step = MARGIN_STEP;

  const currentMargin = getLabelData()?.logo_margin ?? 8;
  slider.value = currentMargin;
  input.value = currentMargin;

  if (valueDisplay) {
    valueDisplay.textContent = currentMargin + "px";
  }

  // Remove old handlers
  if (boundHandlers.has("marginSliderInput")) {
    slider.removeEventListener("input", boundHandlers.get("marginSliderInput"));
  }
  if (boundHandlers.has("marginSliderChange")) {
    slider.removeEventListener("change", boundHandlers.get("marginSliderChange"));
  }

  // Input handler - real-time visual update
  const inputHandler = () => {
    const value = parseInt(slider.value, 10);
    input.value = value;
    if (valueDisplay) {
      valueDisplay.textContent = value + "px";
    }
    updateLogoMarginUI(value);
  };

  // Change handler - persist to server when done sliding
  const changeHandler = () => {
    const value = parseInt(slider.value, 10);
    persistLogoMargin(value).catch(console.error);
  };

  boundHandlers.set("marginSliderInput", inputHandler);
  boundHandlers.set("marginSliderChange", changeHandler);

  slider.addEventListener("input", inputHandler);
  slider.addEventListener("change", changeHandler);

  // Reset button
  if (resetBtn) {
    if (boundHandlers.has("marginResetClick")) {
      resetBtn.removeEventListener("click", boundHandlers.get("marginResetClick"));
    }

    const resetHandler = (e) => {
      e.preventDefault();

      slider.value = DEFAULT_MARGIN_PX;
      input.value = DEFAULT_MARGIN_PX;
      if (valueDisplay) {
        valueDisplay.textContent = DEFAULT_MARGIN_PX + "px";
      }
      updateLogoMarginUI(DEFAULT_MARGIN_PX);
      persistLogoMargin(DEFAULT_MARGIN_PX).catch(console.error);
      resetBtn.classList.add("is-hidden");
    };

    boundHandlers.set("marginResetClick", resetHandler);
    resetBtn.addEventListener("click", resetHandler);
  }
}

async function persistLogoScale(scale) {
  const labelData = getLabelData();
  if (!labelData?.logo) {
    return;
  }

  const scaleStr = scale.toFixed(2);
  labelData.logo_scale = parseFloat(scaleStr);

  const logoId = labelData.logo.id;

  try {
    await fetchLogoScript(getLogoPath(labelData.id), {
      method: "PUT",
      body: JSON.stringify({
        [getParamKey()]: {
          logo_id: logoId,
          logo_scale: scaleStr,
        },
      }),
    });
  } catch (error) {
    handleError("Error saving logo scale. Please try again.", error);
  }
}

async function persistLogoMargin(margin) {
  const labelData = getLabelData();
  if (!labelData?.logo) {
    return;
  }

  const marginInt = parseInt(margin, 10);
  labelData.logo_margin = marginInt;

  const logoId = labelData.logo.id;

  try {
    await fetchLogoScript(getLogoPath(labelData.id), {
      method: "PUT",
      body: JSON.stringify({
        [getParamKey()]: {
          logo_id: logoId,
          logo_margin: marginInt,
        },
      }),
    });
  } catch (error) {
    handleError("Error saving logo margin. Please try again.", error);
  }
}

// ==========================================
// Sidebar controls
// ==========================================

const setupCheckbox = () => {
  const checkbox = document.querySelector(SIDEBAR_LOGO_TOGGLE);
  if (!checkbox) return;

  // Remove old handler
  if (boundHandlers.has("logoToggleChange")) {
    checkbox.removeEventListener("change", boundHandlers.get("logoToggleChange"));
  }

  const changeHandler = () => {
    const hideLogo = checkbox.checked;
    const labelData = getLabelData();
    const scaleSection = document.querySelector(SIDEBAR_LOGO_SCALE_SECTION);

    if (!hideLogo && labelData?.logo) {
      scaleSection?.classList.remove("is-hidden");
      setupSidebarScaleSlider();
      setupSidebarMarginSlider();
    } else if (hideLogo) {
      scaleSection?.classList.add("is-hidden");
    }

    saveHideLogoSetting(hideLogo).catch(console.error);
  };

  boundHandlers.set("logoToggleChange", changeHandler);
  checkbox.addEventListener("change", changeHandler);
};

const setupEditButton = () => {
  const editLink = document.querySelector(SIDEBAR_LOGO_EDIT);
  if (!editLink) return;

  // Remove old handler
  if (boundHandlers.has("editLogoClick")) {
    editLink.removeEventListener("click", boundHandlers.get("editLogoClick"));
  }

  const clickHandler = (e) => {
    e.preventDefault();
    resetUploadForm();
    Modal.show(MODAL);
  };

  boundHandlers.set("editLogoClick", clickHandler);
  editLink.addEventListener("click", clickHandler);
};

// ==========================================
// Logo buttons and modal
// ==========================================

function validateUploadForm() {
  const fileInput = document.querySelector(`${DROPZONE_SELECTOR} .file-input`);
  const submitBtn = document.querySelector(LOGO_UPLOAD_SUBMIT_BTN);
  const nameField = document.querySelector(LOGO_NAME_INPUT)?.closest(".field");

  if (!fileInput || !submitBtn) return;

  const hasFile = fileInput.files.length > 0;
  submitBtn.disabled = !hasFile;

  // Show/hide the logo name field based on file selection
  if (nameField) {
    nameField.classList.toggle("is-hidden", !hasFile);
  }
}

function resetUploadForm() {
  const fileInput = document.querySelector(LOGO_FILE_INPUT);
  const nameInput = document.querySelector(LOGO_NAME_INPUT);
  const selectedFile = document.querySelector(`${DROPZONE_SELECTOR} .selected-file`);
  const nameField = nameInput?.closest(".field");
  const submitBtn = document.querySelector(LOGO_UPLOAD_SUBMIT_BTN);

  if (fileInput) fileInput.value = "";
  if (nameInput) nameInput.value = "Logo";
  if (selectedFile) selectedFile.classList.add("is-hidden");
  if (nameField) nameField.classList.add("is-hidden");
  if (submitBtn) submitBtn.disabled = true;
}

function setupLogoModalButtons() {
  const modal = document.querySelector(MODAL);
  if (!modal) return;

  // Use event delegation for grid buttons to avoid cloning/memory leaks
  if (boundHandlers.has("logoModalClick")) {
    modal.removeEventListener("click", boundHandlers.get("logoModalClick"));
  }

  const modalClickHandler = async (e) => {
    // Handle select button clicks
    const selectBtn = e.target.closest(LOGO_GRID_SELECT_BUTTON);
    if (selectBtn) {
      e.preventDefault();
      const logoId = selectBtn.dataset.logoId;
      const labelData = getLabelData();

      selectBtn.classList.add("is-loading");

      try {
        await fetchLogoScript(getLogoPath(labelData.id), {
          method: "PUT",
          body: JSON.stringify({
            [getParamKey()]: {
              logo_id: logoId,
              hide_logo: false,
            },
          }),
        });
        document.querySelectorAll(LOGO_GRID_SELECT_BUTTON).forEach((b) => {
          b.classList.remove("is-primary");
          b.classList.add("is-info");
          b.textContent = "Select";
        });
        selectBtn.classList.remove("is-info");
        selectBtn.classList.add("is-primary");
        selectBtn.textContent = "Selected";
      } catch (error) {
        handleError(LOGO_UPDATE_ERROR, error);
      } finally {
        selectBtn.classList.remove("is-loading");
      }
      return;
    }

    // Handle delete button clicks
    const deleteBtn = e.target.closest(LOGO_GRID_DELETE_BUTTON);
    if (deleteBtn) {
      e.preventDefault();
      const logoId = deleteBtn.dataset.logoId;
      const logoItem = deleteBtn.closest(LOGO_GRID_ITEM);
      const logoName = logoItem?.querySelector("p")?.textContent || "this logo";
      const labelData = getLabelData();

      if (confirm(`Are you sure you want to delete the logo "${logoName}"?`)) {
        try {
          await fetchLogoScript(getLogosPath(labelData.id) + "/" + logoId, {
            method: "DELETE",
          });
        } catch (error) {
          handleError("Error deleting logo. Please try again.", error);
        }
      }
      return;
    }

    // Handle upload submit button clicks
    const uploadBtn = e.target.closest(LOGO_UPLOAD_SUBMIT_BTN);
    if (uploadBtn) {
      e.preventDefault();

      const name = document.querySelector(LOGO_NAME_INPUT)?.value;
      const fileInputEl = document.querySelector(LOGO_FILE_INPUT);

      if (!name || !fileInputEl?.files[0]) {
        return;
      }

      uploadBtn.classList.add("is-loading");
      uploadBtn.disabled = true;

      const formData = new FormData();
      formData.append("logo[name]", name);
      formData.append("logo[image]", fileInputEl.files[0]);

      try {
        await fetchLogoScript(getLogosPath(getLabelData().id), {
          method: "POST",
          body: formData,
        });
        resetUploadForm();
      } catch (error) {
        handleError("Error uploading logo. Please try again.", error);
        uploadBtn.disabled = false;
      } finally {
        uploadBtn.classList.remove("is-loading");
      }
    }
  };

  boundHandlers.set("logoModalClick", modalClickHandler);
  modal.addEventListener("click", modalClickHandler);

  // File input change handler - use Map-based tracking instead of cloneNode
  const fileInput = document.querySelector(`${DROPZONE_SELECTOR} input.file-input`);
  if (fileInput) {
    if (boundHandlers.has("fileInputChange")) {
      fileInput.removeEventListener("change", boundHandlers.get("fileInputChange"));
    }

    const changeHandler = () => {
      const nameSpan = fileInput.closest(".file-label")?.querySelector(".file-name");
      if (fileInput.files.length) {
        if (nameSpan) {
          nameSpan.textContent = fileInput.files[0].name;
          nameSpan.classList.remove("is-hidden");
        }
      } else {
        if (nameSpan) {
          nameSpan.classList.add("is-hidden");
        }
      }
      validateUploadForm();
    };

    boundHandlers.set("fileInputChange", changeHandler);
    fileInput.addEventListener("change", changeHandler);
  }
}

// ==========================================
// Display & visibility
// ==========================================

function setLogoVisibility({ isVisible }) {
  const logoEdit = document.querySelector(SIDEBAR_LOGO_EDIT);
  const wrapper = document.querySelector(LOGO_WRAPPER);
  const hint = document.querySelector(SIDEBAR_LOGO_HINT);

  if (logoEdit) logoEdit.classList.toggle("is-hidden", !isVisible);
  if (wrapper) wrapper.classList.toggle("is-hidden", !isVisible);
  if (hint) hint.classList.toggle("is-hidden", isVisible);
}

async function saveHideLogoSetting(hideLogo) {
  const labelData = getLabelData();
  labelData.hide_logo = hideLogo;
  setLogoVisibility({ isVisible: !hideLogo });

  const scaleSection = document.querySelector(SIDEBAR_LOGO_SCALE_SECTION);

  if (hideLogo) {
    scaleSection?.classList.add("is-hidden");
  } else if (labelData.logo) {
    scaleSection?.classList.remove("is-hidden");
    setupSidebarScaleSlider();
    setupSidebarMarginSlider();
  }

  try {
    await fetchLogoScript(getToggleDisplayPath(labelData.id), {
      method: "PUT",
      body: JSON.stringify({ hide_logo: hideLogo }),
    });
  } catch (error) {
    handleError("Error updating logo visibility. Please try again.", error);
    setLogoVisibility({ isVisible: !getLabelData().hide_logo });
  }
}

const renderLabelLogo = () => {
  const wrapper = document.querySelector(LOGO_WRAPPER);
  if (!wrapper) return;

  const labelData = getLabelData();
  if (labelData.hide_logo) {
    wrapper.classList.add("is-hidden");
  } else {
    const logoData = labelData.logo;
    if (logoData?.image_url) {
      wrapper.classList.remove("is-hidden");
      updateLogoScaleUI(getScale());
      updateLogoMarginUI(getMargin());
    }
  }
};

// ==========================================
// Initialization & setup
// ==========================================

const setup = (labelType = "recipe") => {
  // Set the config for this label type
  currentConfig = LABEL_CONFIGS[labelType] || LABEL_CONFIGS.recipe;

  const labelData = getLabelData();
  if (!labelData) {
    console.warn("[logo.js] No label data found for type:", labelType);
    return;
  }

  const hideLogo = labelData.hide_logo;
  const checkbox = document.querySelector(SIDEBAR_LOGO_TOGGLE);
  if (checkbox) {
    checkbox.checked = hideLogo;
  }

  setLogoVisibility({ isVisible: !hideLogo });

  const hasLogo = labelData.logo != null;
  const isLogoVisible = !hideLogo;
  const scaleSection = document.querySelector(SIDEBAR_LOGO_SCALE_SECTION);

  if (hasLogo && isLogoVisible) {
    scaleSection?.classList.remove("is-hidden");
    setupSidebarScaleSlider();
    setupSidebarMarginSlider();
    updateSizeResetButtonVisibility();
    updateMarginResetButtonVisibility();
  } else {
    scaleSection?.classList.add("is-hidden");
  }

  renderLabelLogo();

  setTimeout(() => {
    // After initial load, set fade-in duration to fast for partial:replace updates (mainly for Label Type switch)
    document.documentElement.style.setProperty("--logo-fade-in-duration", "0.5s");
  }, 1000);

  setupCheckbox();
  setupEditButton();
  setupLogoModalButtons();

  // Listen for extrasections:opened custom event
  const extrasections = document.querySelector("#extrasections");
  if (extrasections) {
    // Remove old handler
    if (boundHandlers.has("extrasectionsOpened")) {
      extrasections.removeEventListener(
        "extrasections:opened",
        boundHandlers.get("extrasectionsOpened")
      );
    }

    const openedHandler = () => {
      if (hasLogo && isLogoVisible) {
        scaleSection?.classList.remove("is-hidden");
        setupSidebarScaleSlider();
        setupSidebarMarginSlider();
      }
    };

    boundHandlers.set("extrasectionsOpened", openedHandler);
    extrasections.addEventListener("extrasections:opened", openedHandler);
  }
};

// Export setup for external initialization (e.g., supplement labels)
export { setup as setupLogo };

// Track partial:replace handler to prevent listener accumulation
let partialReplaceHandler = null;

export function initializeRecipeLogo() {
  setup("recipe");

  // Remove previous listener if exists to prevent accumulation
  if (partialReplaceHandler) {
    document.removeEventListener("partial:replace", partialReplaceHandler);
  }
  partialReplaceHandler = () => setup("recipe");
  document.addEventListener("partial:replace", partialReplaceHandler);
}

// For supplement labels, re-initialize on partial:replace
document.addEventListener("partial:replace", (e) => {
  if (currentConfig === LABEL_CONFIGS.supplement && window.supplement_label) {
    initializeSupplementLogoModal();
  }
});

// For supplement labels, Vue handles the sidebar but we need to initialize modal bindings
// when the modal is opened. This function sets the config and prepares the modal.
export function initializeSupplementLogoModal() {
  currentConfig = LABEL_CONFIGS.supplement;

  // Ensure window.supplement_label exists
  const labelData = getLabelData();
  if (!labelData) {
    console.warn("[logo.js] Cannot initialize supplement modal: window.supplement_label not set");
    return;
  }

  setupEditButton();
  setupCheckbox();
  resetUploadForm();
  setupLogoModalButtons();
}
